import {
    type StationCreationForm,
    type WizardVariant,
    makeInitialStationForm,
} from './stationCreationForm';

/**
 * The wizard's draft, so a reload does not throw the whole setup away.
 *
 * Creating a station needs a Product, and a tenant who has none is sent to
 * Services to make one. That link opens a second tab, so the wizard itself
 * survives — but before #2441 the new Product never appeared in the wizard's
 * picker (separate Redux store, no cross-tab invalidation), so the way out was a
 * reload, and the reload discarded the location, the address, the station and
 * every object row. That is what "we lost everything" was.
 *
 * `LinkProductStep` now refetches on window focus, so the reload is no longer
 * needed for that (verified on integration, QA #2439 scenario 18). The draft
 * stays: a reload still happens for a dozen ordinary reasons, and losing a
 * half-filled wizard to any of them is the same loss.
 *
 * `sessionStorage`, not `localStorage`: a draft belongs to the tab it is being
 * typed in, and should not resurface days later in an unrelated session.
 * Keyed per tenant, because a master switching tenants must never see the
 * previous tenant's entries (CLAUDE.md, "Reset State on Tenant / Context
 * Switch").
 *
 * Every read and write is wrapped: `sessionStorage` throws outright in some
 * contexts (private windows, blocked site data), and losing a draft must never
 * take the page down with it.
 */

const KEY_PREFIX = 'kolula:station-draft:';

interface StoredDraft {
    form: StationCreationForm;
    step: number;
}

/**
 * Per variant as well: a half-typed house must not reopen as a station draft,
 * or the other way round. The station key keeps its old shape, so a draft
 * written before houses existed still restores.
 */
const keyFor = (tenant: string, variant: WizardVariant): string => (
    variant === 'house' ? `${KEY_PREFIX}house:${tenant}` : `${KEY_PREFIX}${tenant}`
);

export const saveStationDraft = (
    tenant: string | null | undefined,
    form: StationCreationForm,
    step: number,
    variant: WizardVariant = 'station',
): void => {
    if (!tenant) return;
    try {
        sessionStorage.setItem(keyFor(tenant, variant), JSON.stringify({ form, step }));
    } catch {
        // A draft is a convenience; never let storage failure break the wizard.
    }
};

export const clearStationDraft = (
    tenant: string | null | undefined,
    variant: WizardVariant = 'station',
): void => {
    if (!tenant) return;
    try {
        sessionStorage.removeItem(keyFor(tenant, variant));
    } catch {
        // as above
    }
};

/**
 * The stored draft for this tenant, or null. Merged onto a fresh form so a
 * draft written by an older version of the wizard cannot leave a new field
 * undefined — the shape is the current one, the stored values fill it in.
 */
export const readStationDraft = (
    tenant: string | null | undefined,
    variant: WizardVariant = 'station',
): { form: StationCreationForm, step: number } | null => {
    if (!tenant) return null;
    try {
        const raw = sessionStorage.getItem(keyFor(tenant, variant));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<StoredDraft>;
        if (!parsed || typeof parsed !== 'object' || !parsed.form) return null;
        const form = { ...makeInitialStationForm(), ...parsed.form };
        // A draft written before #2280 has no `productRows` at all, and the
        // merge above covers that. An empty or non-array one is the case the
        // merge does NOT cover — `{...fresh, ...stored}` lets a stored `[]` win
        // — and it would render a product step with nothing in it, which reads
        // as a broken screen rather than a stale draft. Checked with
        // `Array.isArray`, never `?? []`: `{}` is truthy.
        if (!Array.isArray(form.productRows) || form.productRows.length === 0) {
            form.productRows = makeInitialStationForm().productRows;
        }
        return {
            form,
            step: typeof parsed.step === 'number' ? parsed.step : 0,
        };
    } catch {
        return null;
    }
};
